import { createNodeFromContent, getHTMLFromFragment } from '@tiptap/core'
import type { Editor, JSONContent } from '@tiptap/react'
import DOMPurify from 'dompurify'
import { isEqual } from 'lodash-es'
import { areHtmlEqual, stripEmptyEdgeParagraphs } from '#src/utils/html.ts'

export type EditorOutputFormat = 'html' | 'json'

/* Serialize the editor's current content to the chosen persistence format.
 *
 * - 'html' (default): sanitize via DOMPurify, strip empty edge paragraphs, and
 *   collapse a single wrapping <p>...</p> so callers that store one-line
 *   content don't accumulate block wrappers.
 * - 'json': JSON.stringify(editor.getJSON()). Skips DOMPurify because TipTap
 *   JSON is structured data — readers convert it back to HTML through
 *   RENDER_EXTENSIONS before rendering, at which point extension-level
 *   serialization (which already enforces a safe HTML shape) applies. */
export function getCleanedContent(
  editor: Editor,
  outputFormat: EditorOutputFormat
): string {
  if (outputFormat === 'json') {
    return JSON.stringify(editor.getJSON())
  }
  return cleanEditorHtml(editor.getHTML())
}

export function stripPTagIfOneLine(html: string): string {
  if (html.match(/<p>/g)?.length !== 1) {
    return html
  }
  return html.replace(/<\/?p>/g, '')
}

/* Shared cleaning pipeline for BOTH sides of the mount-time reconcile compare:
 * sanitize, strip empty edge paragraphs, then collapse a lone <p> wrapper. The
 * edge-paragraph strip neutralizes StarterKit's TrailingNode — it appends an
 * empty trailing <p> to the LIVE editor doc (whenever the doc ends in a
 * non-paragraph node such as <ul>) but is NOT produced by a bare
 * createNodeFromContent parse, so the live and incoming sides would otherwise
 * never match.
 *
 * Order matters: stripEmptyEdgeParagraphs runs BEFORE stripPTagIfOneLine (the
 * same order as the getCleanedContent save path). If the lone-<p> collapse ran
 * first, a stray trailing empty <p> on one side would leave two <p> tags
 * (blocking the collapse) while the other side, with a single <p>, collapsed to
 * bare text — an asymmetry that spuriously fires on single-paragraph notes.
 * Running both sides through this identical pipeline leaves only genuine content
 * drift visible to areHtmlEqual. */
export function cleanEditorHtml(html: string): string {
  return stripPTagIfOneLine(stripEmptyEdgeParagraphs(DOMPurify.sanitize(html)))
}

/* Project incoming content (HTML string or TipTap JSON) through the editor's own
 * schema and back to cleaned HTML — the "incoming" side of reconcile compares.
 *
 * Re-parsing through the schema is what makes the compare fair: the ProseMirror
 * DOMParser drops attributes the schema doesn't declare (e.g. the vestigial
 * `data-choice-id` baked into legacy RN-tool templates), exactly as the live
 * editor's own parse does. `preserveWhitespace: true` mirrors the editor-level
 * parseOptions (and the setContent call sites) so block-edge whitespace survives
 * on both sides instead of being asymmetrically collapsed; it is ignored for JSON
 * input, which skips DOM parsing entirely (schema.nodeFromJSON). */
export function canonicalizeForReconcile(
  editor: Editor,
  content: string | JSONContent
): string {
  const parsed = createNodeFromContent(content, editor.schema, {
    slice: false,
    parseOptions: { preserveWhitespace: true },
    errorOnInvalidContent: false,
  })
  /* `slice: false` yields a ProseMirror doc node; `.content` is its fragment. A
   * ProseMirror node exposes `type`; a bare Fragment does not — narrow on that. */
  const fragment = 'type' in parsed ? parsed.content : parsed
  return cleanEditorHtml(getHTMLFromFragment(fragment, editor.schema))
}

/* Whether a reconcile (mount-time Medplum -> Liveblocks, or useSyncEditorContent)
 * should replace the editor doc with `initialContent` (HTML string or TipTap
 * JSON). Both sides are canonicalized into HTML space (see
 * canonicalizeForReconcile) so the compare ignores schema-dropped attributes,
 * whitespace-parse differences, and the TrailingNode empty <p>, and fires only
 * on genuine text/structure/declared-attribute drift. JSON goes through the same
 * HTML-space canonicalization because a structural getJSON() compare never
 * converges for normalized docs (e.g. an empty editor holds one paragraph that
 * `{type:'doc',content:[]}` lacks), which re-fires setContent every render.
 *
 * Fail-open: if `initialContent` can't be projected through the schema, fall
 * back to the pre-canonicalization compare (raw HTML / structural isEqual) so a
 * projection failure is never worse than the behavior before this fix. */
export function shouldReconcileEditorContent(
  editor: Editor,
  initialContent: string | JSONContent
): boolean {
  const currentRawHtml = editor.getHTML()
  try {
    // Both sides run through the SAME cleanEditorHtml pipeline so only genuine
    // drift survives the compare.
    const currentHtml = cleanEditorHtml(currentRawHtml)
    const incomingHtml = canonicalizeForReconcile(editor, initialContent)
    return !areHtmlEqual(currentHtml, incomingHtml)
  } catch {
    // The JSON fallback reverts to the structural compare, which never converges
    // for normalized docs (the original bug shape). That cannot loop from here:
    // this catch is only reachable when the schema projection itself is broken —
    // tiptap absorbs invalid JSON internally with errorOnInvalidContent:false —
    // and a broken schema makes setContent throw before dispatching a
    // transaction (no re-render pump), while memoized content identity upstream
    // means the sync effect does not re-arm per render.
    if (typeof initialContent !== 'string') {
      return !isEqual(editor.getJSON(), initialContent)
    }
    // Fail-open to the historical compare: getCleanedHtml (sanitize + lone-<p>
    // collapse, no edge strip) vs the raw initialContent.
    return !areHtmlEqual(
      stripPTagIfOneLine(DOMPurify.sanitize(currentRawHtml)),
      initialContent
    )
  }
}
